import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, MessageCircle, LayoutDashboard, LogOut, User as UserIcon, Globe, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { Avatar } from "@/components/Avatar";
import { NotificationBell } from "@/components/NotificationBell";

export const LangToggle = () => {
  const { lang, setLang } = useLanguage();
  const langs = ["pt", "en", "fr", "es", "de", "it"];
  return (
    <>
      <div className="hidden md:flex items-center rounded-full border border-slate-200 bg-white p-0.5" data-testid="lang-toggle">
        {langs.map((l) => (
          <button
            key={l}
            data-testid={`lang-${l}`}
            onClick={() => setLang(l)}
            className={`px-2.5 py-1 text-xs font-semibold uppercase rounded-full transition-colors duration-200 ${lang === l ? "bg-primary text-white" : "text-slate-500 hover:text-primary"}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full border border-slate-200 bg-white" data-testid="lang-globe-button" aria-label="Idioma">
              <Globe size={18} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" data-testid="lang-menu">
            {langs.map((l) => (
              <DropdownMenuItem key={l} data-testid={`lang-mobile-${l}`} onClick={() => setLang(l)} className={`uppercase font-semibold ${lang === l ? "text-accent" : ""}`}>
                {l}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navItems = [
    { to: "/pesquisa", label: t("nav.search"), testid: "nav-search" },
    { to: "/publicar-viagem", label: t("nav.publishTrip"), testid: "nav-publish-trip" },
    { to: "/enviar-encomenda", label: t("nav.sendParcel"), testid: "nav-send-parcel" },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
          <img src="/icons/icon-192.png" alt="Boleia Connect" className="w-9 h-9 rounded-xl" />
          <span className="font-heading font-bold text-lg text-primary">Boleia<span className="text-accent"> Connect</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((i) => (
            <NavLink key={i.to} to={i.to} data-testid={i.testid} className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${isActive ? "bg-secondary text-primary" : "text-slate-600 hover:text-primary hover:bg-slate-50"}`}>
              {i.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LangToggle />
          {user ? (
            <>
              <NotificationBell key={user.id} />
              <Button variant="ghost" size="icon" className="hidden md:inline-flex rounded-full" data-testid="nav-messages" onClick={() => navigate("/mensagens")}>
                <MessageCircle size={18} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" data-testid="user-menu-button" className="hidden md:flex rounded-full gap-2">
                    <Avatar user={user} size="w-7 h-7" text="text-xs" testid="header-avatar" />
                    <span className="max-w-[120px] truncate">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/painel")}><LayoutDashboard size={16} className="mr-2" />{t("nav.dashboard")}</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><ShieldCheck size={16} className="mr-2" />{t("adminPanel.nav")}</DropdownMenuItem>}
                  <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate("/mensagens")}><MessageCircle size={16} className="mr-2" />{t("nav.messages")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/utilizador/${user.id}`)}><UserIcon size={16} className="mr-2" />{t("common.seeProfile")}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout}><LogOut size={16} className="mr-2" />{t("nav.logout")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button variant="ghost" data-testid="nav-login" className="rounded-full" onClick={() => navigate("/entrar")}>{t("nav.login")}</Button>
              <Button data-testid="nav-register" className="rounded-full bg-accent hover:bg-accent/90 text-white" onClick={() => navigate("/registar")}>{t("nav.register")}</Button>
            </div>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu-button"><Menu size={20} /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72" aria-describedby={undefined}>
              <SheetTitle className="font-heading text-primary">Boleia Connect</SheetTitle>
              <div className="flex flex-col gap-2 mt-6">
                {navItems.map((i) => (
                  <Button key={i.to} variant="ghost" data-testid={`mobile-${i.testid}`} className="justify-start rounded-xl" onClick={() => { setOpen(false); navigate(i.to); }}>{i.label}</Button>
                ))}
                {user ? (
                  <>
                    <Button variant="ghost" data-testid="mobile-nav-dashboard" className="justify-start rounded-xl" onClick={() => { setOpen(false); navigate("/painel"); }}>{t("nav.dashboard")}</Button>
                    {user.role === "admin" && <Button variant="ghost" data-testid="mobile-nav-admin" className="justify-start rounded-xl" onClick={() => { setOpen(false); navigate("/admin"); }}>{t("adminPanel.nav")}</Button>}
                    <Button variant="ghost" data-testid="mobile-nav-messages" className="justify-start rounded-xl" onClick={() => { setOpen(false); navigate("/mensagens"); }}>{t("nav.messages")}</Button>
                    <Button variant="ghost" data-testid="mobile-nav-logout" className="justify-start rounded-xl text-destructive" onClick={() => { setOpen(false); logout(); }}>{t("nav.logout")}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" data-testid="mobile-nav-login" className="rounded-xl" onClick={() => { setOpen(false); navigate("/entrar"); }}>{t("nav.login")}</Button>
                    <Button data-testid="mobile-nav-register" className="rounded-xl bg-accent hover:bg-accent/90 text-white" onClick={() => { setOpen(false); navigate("/registar"); }}>{t("nav.register")}</Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-primary text-white mt-24">
      <div className="max-w-7xl mx-auto px-6 py-12 grid gap-10 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src="/icons/icon-192.png" alt="Boleia Connect" className="w-9 h-9 rounded-xl" />
            <span className="font-heading font-bold text-lg">Boleia<span className="text-accent"> Connect</span></span>
          </div>
          <p className="text-sm text-white/70">{t("tagline")}</p>
          <p className="text-xs text-white/50 mt-4">{t("home.noPayments")}</p>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <Link to="/pesquisa" data-testid="footer-search" className="text-white/80 hover:text-white transition-colors duration-200">{t("footer.search")}</Link>
          <Link to="/publicar-viagem" data-testid="footer-publish" className="text-white/80 hover:text-white transition-colors duration-200">{t("footer.publish")}</Link>
          <Link to="/enviar-encomenda" data-testid="footer-parcel" className="text-white/80 hover:text-white transition-colors duration-200">{t("footer.parcel")}</Link>
          <Link to="/privacidade" data-testid="footer-privacy" className="text-white/80 hover:text-white transition-colors duration-200">{t("footer.privacy")}</Link>
          <Link to="/termos" data-testid="footer-terms" className="text-white/80 hover:text-white transition-colors duration-200">{t("footer.terms")}</Link>
          <Link to="/faq" data-testid="footer-faq" className="text-white/80 hover:text-white transition-colors duration-200">{t("footer.faq")}</Link>
          <Link to="/ajuda" data-testid="footer-help" className="text-white/80 hover:text-white transition-colors duration-200">{t("footer.help")}</Link>
        </div>
        <div className="text-sm text-white/60 md:text-right">
          <p>{t("footer.made")}</p>
          <p className="mt-2">© {new Date().getFullYear()} Boleia Connect. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};
